import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { isApiClientError } from "../api/errors";
import type {
  DocumentWorkflowMailMessageDetail,
  DocumentWorkflowOutbox,
} from "../entities/document-workflow";
import {
  addressListError,
  mergeAddresses,
  splitAddresses,
} from "../entities/counterparties";
import { newIdempotencyKey } from "../api/idempotency";
import { AttachmentPicker, type PickedAttachment } from "./AttachmentPicker";
import { CloseButton } from "./CloseButton";
import { CounterpartyRecipientPicker } from "./CounterpartyRecipientPicker";
import { safeMailHtml } from "./safeMailHtml";
import type { ShellServices } from "./services";
import { Select } from "./Select";
import { Notice } from "./Notice";

type Confirmation =
  | { kind: "retry"; outboxId: string; uncertain: boolean }
  | { kind: "cancel"; outboxId: string }
  | { kind: "split"; threadId: string }
  | { kind: "merge"; threadId: string };
type ThreadConfirmation = Extract<Confirmation, { kind: "split" | "merge" }>;

const outboxLabels: Record<DocumentWorkflowOutbox["status"], string> = {
  draft: "Черновик",
  queued: "В очереди",
  sending: "Отправляется",
  sent: "Отправлено",
  failed: "Ошибка отправки",
  cancelled: "Отменено",
  delivery_uncertain: "Доставка не подтверждена",
};

function messageTime(message: DocumentWorkflowMailMessageDetail): number {
  return new Date(message.received_at ?? message.sent_at ?? 0).getTime();
}

function errorText(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.code === "document_workflow.mail_delivery_uncertain")
      return "Доставка не подтверждена. Повтор может привести к дубликату у получателя.";
    if (error.status === 403) return "Недостаточно прав для этого действия.";
    if (error.status === 409)
      return "Состояние письма уже изменилось. Обновите цепочку.";
  }
  return "Не удалось выполнить действие. Повторите попытку.";
}

function person(value: { email: string; name: string | null } | null): string {
  return value === null
    ? "не указан"
    : value.name === null || value.name === ""
      ? value.email
      : `${value.name} <${value.email}>`;
}

function people(values: { email: string; name: string | null }[]): string {
  return values.length === 0 ? "не указаны" : values.map(person).join(", ");
}

function MailBody({ message }: { message: DocumentWorkflowMailMessageDetail }) {
  if (message.sanitized_html_body) {
    return (
      <iframe
        className="mail-thread__html"
        title="Безопасное содержимое письма"
        sandbox=""
        referrerPolicy="no-referrer"
        srcDoc={safeMailHtml(message.sanitized_html_body)}
      />
    );
  }
  return (
    <p className="mail-thread__text">
      {message.text_body || "В письме нет текстового содержимого."}
    </p>
  );
}

export function DocumentMailThread({
  documentId,
  accessScopeId,
  services,
  initialMessageId,
  unreadCount,
  canReply = false,
  canManageThreads = false,
  canViewFiles = false,
  canLinkFiles = false,
}: {
  documentId: string;
  accessScopeId?: string;
  services: ShellServices;
  initialMessageId?: string;
  unreadCount?: number;
  canReply?: boolean;
  canManageThreads?: boolean;
  canViewFiles?: boolean;
  canLinkFiles?: boolean;
}) {
  const client = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(
    initialMessageId ?? null,
  );
  const [showRead, setShowRead] = useState(true);
  const [compose, setCompose] = useState(false);
  const [to, setTo] = useState("");
  const [bcc, setBcc] = useState("");
  // Сообщения о неверных адресах показываются рядом с полем и называют адрес.
  const [addressErrors, setAddressErrors] = useState<{
    to: string | null;
    bcc: string | null;
  }>({ to: null, bcc: null });
  const toInput = useRef<HTMLInputElement>(null);
  const bccInput = useRef<HTMLInputElement>(null);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<readonly PickedAttachment[]>(
    [],
  );
  const [splitIds, setSplitIds] = useState<readonly string[]>([]);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [reason, setReason] = useState("");
  const [mergeSourceId, setMergeSourceId] = useState("");
  const [latestOutbox, setLatestOutbox] =
    useState<DocumentWorkflowOutbox | null>(null);
  const [linkedDocumentId, setLinkedDocumentId] = useState("");
  const [existingFileId, setExistingFileId] = useState("");
  const [attachmentTargetId, setAttachmentTargetId] = useState("");
  const [linkedVersion, setLinkedVersion] = useState<{
    attachmentId: string;
    versionId: string;
    versionNumber: number;
  } | null>(null);
  const submissionKey = useRef<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (confirmation) reasonRef.current?.focus();
  }, [confirmation]);

  const messages = useInfiniteQuery({
    queryKey: ["document-workflow", "documents", documentId, "mail-pages"],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      services.documentWorkflow.messages(documentId, pageParam),
    getNextPageParam: (last) => last.meta.next_cursor ?? undefined,
    retry: false,
  });
  // Новые письма сверху, как и в обсуждении: свежее читают чаще, чем начало цепочки.
  const entries = useMemo(
    () =>
      [...(messages.data?.pages.flatMap((page) => page.data) ?? [])].sort(
        (left, right) => messageTime(right) - messageTime(left),
      ),
    [messages.data],
  );
  const selectedBelongs = entries.some((entry) => entry.id === selectedId);
  useEffect(() => {
    if (
      initialMessageId &&
      !entries.some((entry) => entry.id === initialMessageId) &&
      messages.hasNextPage &&
      !messages.isFetchingNextPage
    )
      void messages.fetchNextPage();
  }, [initialMessageId, entries, messages]);
  const threads = useQuery({
    queryKey: ["document-workflow", "documents", documentId, "mail-threads"],
    queryFn: () => services.documentWorkflow.threads(documentId),
    retry: false,
  });
  const message = useQuery({
    queryKey: ["document-workflow", "mail-message", selectedId],
    queryFn: () => services.documentWorkflow.message(selectedId ?? ""),
    enabled: selectedId !== null && selectedBelongs && canViewFiles,
    retry: false,
  });
  const messageAttachments = useQuery({
    queryKey: ["document-workflow", "mail-message", selectedId, "attachments"],
    queryFn: () => services.documentWorkflow.attachments(selectedId ?? ""),
    enabled: selectedId !== null && selectedBelongs,
    retry: false,
  });
  const outbox = useQuery({
    queryKey: ["document-workflow", "outbox", latestOutbox?.id],
    queryFn: () => services.documentWorkflow.outbox(latestOutbox?.id ?? ""),
    enabled: latestOutbox !== null,
    retry: false,
    refetchInterval: (query) =>
      ["queued", "sending"].includes(
        query.state.data?.status ?? latestOutbox?.status ?? "",
      )
        ? 2_000
        : false,
  });
  const documentFiles = useQuery({
    queryKey: ["document-workflow", "documents", documentId, "files"],
    queryFn: () => services.documentWorkflow.documentFiles(documentId),
    enabled: canViewFiles && (compose || (selectedId !== null && canLinkFiles)),
    retry: false,
  });

  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ["document-workflow"] });
  };
  const reply = useMutation({
    mutationFn: () =>
      services.documentWorkflow.reply(
        documentId,
        {
          reply_to_message_id: selectedId,
          to: splitAddresses(to),
          bcc: splitAddresses(bcc),
          subject: subject.trim(),
          text: text.trim() || undefined,
          attachment_file_ids: attachments.map((file) => file.id),
        },
        submissionKey.current ??
          (submissionKey.current = newIdempotencyKey()),
      ),
    onSuccess: async (created) => {
      submissionKey.current = null;
      setLatestOutbox(created);
      setCompose(false);
      setText("");
      setAttachments([]);
      await refresh();
    },
  });
  const outboxAction = useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: string;
      action: "retry" | "confirm-retry" | "cancel";
    }) =>
      action === "cancel"
        ? services.documentWorkflow.cancelOutbox(id, reason.trim())
        : services.documentWorkflow.retryOutbox(
            id,
            reason.trim(),
            action === "confirm-retry",
          ),
    onSuccess: async (updated) => {
      setLatestOutbox(updated);
      setConfirmation(null);
      setReason("");
      await refresh();
    },
  });
  const threadAction = useMutation({
    mutationFn: async (action: ThreadConfirmation) =>
      action.kind === "split"
        ? services.documentWorkflow.splitThread(
            action.threadId,
            [...splitIds],
            reason.trim(),
          )
        : services.documentWorkflow.mergeThread(
            action.threadId,
            mergeSourceId,
            reason.trim(),
          ),
    onSuccess: async () => {
      setConfirmation(null);
      setReason("");
      setSplitIds([]);
      setMergeSourceId("");
      await refresh();
    },
  });
  const markRead = useMutation({
    mutationFn: (id: string) => services.documentWorkflow.markMessageRead(id),
    onSuccess: refresh,
  });
  const markThreadRead = useMutation({
    mutationFn: (id: string) => services.documentWorkflow.markThreadRead(id),
    onSuccess: refresh,
  });
  const linkAttachment = useMutation({
    mutationFn: (attachment: { id: string; name: string }) =>
      services.documentWorkflow.linkMailAttachment(documentId, attachment.id, {
        ...(attachmentTargetId
          ? { documentFileId: attachmentTargetId }
          : { displayName: attachment.name }),
      }),
    onSuccess: async (version, attachment) => {
      setLinkedVersion({
        attachmentId: attachment.id,
        versionId: version.id,
        versionNumber: version.version_number,
      });
      setAttachmentTargetId("");
      await refresh();
    },
  });
  const unlinkAttachment = useMutation({
    mutationFn: (versionId: string) =>
      services.documentWorkflow.unlinkDocumentVersion(versionId),
    onSuccess: async () => {
      setLinkedVersion(null);
      await refresh();
    },
  });
  const downloadAttachment = useMutation({
    mutationFn: (attachment: { id: string; name: string }) =>
      services.documentWorkflow.downloadMailAttachment(attachment.id),
    onSuccess: (download, attachment) => {
      const anchor = window.document.createElement("a");
      anchor.href = download.url;
      anchor.download = attachment.name;
      anchor.rel = "noopener";
      anchor.click();
    },
  });
  const link = useMutation({
    mutationFn: () =>
      services.documentWorkflow.linkMessage(selectedId ?? "", linkedDocumentId),
    onSuccess: async () => {
      setLinkedDocumentId("");
      await refresh();
    },
  });
  const unlink = useMutation({
    mutationFn: () =>
      services.documentWorkflow.unlinkMessage(selectedId ?? "", documentId),
    onSuccess: async () => {
      setSelectedId(null);
      await refresh();
    },
  });

  const visible = showRead ? entries : entries.filter((entry) => entry.unread);
  const selected = selectedBelongs ? message.data : undefined;
  const shownOutbox = outbox.data ?? latestOutbox;
  const parent = selected?.in_reply_to
    ? entries.find((entry) => entry.message_id === selected.in_reply_to)
    : undefined;
  const open = (entry: DocumentWorkflowMailMessageDetail) => {
    setSelectedId(entry.id);
    setSubject(entry.subject ?? "");
    if (entry.unread) markRead.mutate(entry.id);
  };
  const toggleSplit = (id: string) =>
    setSplitIds((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );
  const existingFiles = documentFiles.data as unknown as
    | {
        id: string;
        display_name: string;
        current_version: {
          file_id: string | null;
          size: number | null;
          version_number: number;
        } | null;
      }[]
    | undefined;
  const addExistingFile = () => {
    const file = existingFiles?.find(
      (candidate) => candidate.current_version?.file_id === existingFileId,
    );
    const version = file?.current_version;
    const fileId = version?.file_id;
    const fileSize = version?.size ?? 0;
    if (
      file !== undefined &&
      fileId !== null &&
      fileId !== undefined &&
      !attachments.some((attachment) => attachment.id === fileId)
    ) {
      setAttachments((current) => [
        ...current,
        {
          id: fileId,
          filename: file.display_name,
          size: fileSize,
          versionNumber: file.current_version?.version_number,
        },
      ]);
      submissionKey.current = null;
    }
    setExistingFileId("");
  };

  return (
    <section className="mail-thread" aria-labelledby="mail-thread-title">
      {initialMessageId &&
      !selectedBelongs &&
      !messages.isPending &&
      !messages.hasNextPage ? (
        <Notice>
          Письмо из ссылки не найдено в доступной переписке этого документа.
        </Notice>
      ) : null}
      {messages.hasNextPage ? (
        <button
          type="button"
          disabled={messages.isFetchingNextPage}
          onClick={() => void messages.fetchNextPage()}
        >
          Ещё письма
        </button>
      ) : null}
      <header className="mail-thread__header">
        <div>
          <h3 id="mail-thread-title">Внешняя переписка</h3>
          <p className="dw-secondary">
            {entries.length} писем ·{" "}
            <span className="mail-thread__count">
              {unreadCount ?? entries.filter((entry) => entry.unread).length}
            </span>{" "}
            непрочитанных
          </p>
        </div>
        <div className="mail-thread__actions">
          {(unreadCount ?? entries.filter((entry) => entry.unread).length) >
          0 ? (
            <button
              className="button button--secondary button--compact"
              type="button"
              onClick={() => {
                const first =
                  entries.find((entry) => entry.id === initialMessageId) ??
                  // «Первое» — самое давнее из непрочитанных, а лента идёт от новых.
                  [...entries].reverse().find((entry) => entry.unread);
                if (first) open(first);
              }}
            >
              К первому непрочитанному
            </button>
          ) : null}
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={() => setShowRead((value) => !value)}
          >
            {showRead ? "Скрыть прочитанные" : "Показать все"}
          </button>
          {canReply ? (
            <button
              className="button button--primary button--compact"
              type="button"
              aria-expanded={compose}
              onClick={() => setCompose((value) => !value)}
            >
              Ответить
            </button>
          ) : null}
        </div>
      </header>
      {messages.isPending ? (
        <p aria-busy="true">Загружаем письма…</p>
      ) : messages.isError ? (
        <Notice>
          {errorText(messages.error)}
        </Notice>
      ) : visible.length === 0 ? (
        <p className="dw-secondary">
          В цепочке нет писем по выбранному фильтру.
        </p>
      ) : (
        <ol className="mail-thread__list">
          {visible.map((entry) => {
            const entryParent = entry.in_reply_to
              ? entries.find(
                  (candidate) => candidate.message_id === entry.in_reply_to,
                )
              : undefined;
            return (
              <li
                key={entry.id}
                className={
                  entry.unread
                    ? "mail-thread__item mail-thread__item--unread"
                    : "mail-thread__item"
                }
              >
                {canManageThreads ? (
                  <label className="mail-thread__split">
                    <input
                      type="checkbox"
                      checked={splitIds.includes(entry.id)}
                      onChange={() => toggleSplit(entry.id)}
                      aria-label={`Выбрать письмо ${entry.subject ?? "без темы"} для разделения`}
                    />
                  </label>
                ) : null}
                <button
                  type="button"
                  className="mail-thread__open"
                  onClick={() => open(entry)}
                >
                  <strong>{entry.subject ?? "Без темы"}</strong>
                  <span>
                    {person(entry.from)} → {people(entry.to)}
                  </span>
                  {entryParent ? (
                    <span className="mail-thread__parent">
                      Ответ на «{entryParent.subject ?? "Без темы"}» от{" "}
                      {person(entryParent.from)}
                    </span>
                  ) : null}
                  <time>
                    {new Intl.DateTimeFormat("ru-RU", {
                      dateStyle: "short",
                      timeStyle: "short",
                    }).format(
                      new Date(
                        entry.received_at ?? entry.sent_at ?? Date.now(),
                      ),
                    )}
                  </time>
                </button>
                {entry.unread ? (
                  <span className="mail-thread__unread">Новое</span>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
      {shownOutbox ? (
        <aside className="mail-thread__outbox" aria-live="polite">
          <strong>{outboxLabels[shownOutbox.status]}</strong>
          <span>{shownOutbox.subject}</span>
          <span>Попыток: {shownOutbox.attempts}</span>
          {["failed", "delivery_uncertain"].includes(shownOutbox.status) ? (
            <button
              className="button button--secondary button--compact"
              type="button"
              onClick={() =>
                setConfirmation({
                  kind: "retry",
                  outboxId: shownOutbox.id,
                  uncertain: shownOutbox.status === "delivery_uncertain",
                })
              }
            >
              Повторить
            </button>
          ) : null}
          {["draft", "queued", "failed"].includes(shownOutbox.status) ? (
            <button
              className="button button--text button--compact"
              type="button"
              onClick={() =>
                setConfirmation({ kind: "cancel", outboxId: shownOutbox.id })
              }
            >
              Отменить отправку
            </button>
          ) : null}
        </aside>
      ) : null}
      {canManageThreads ? (
        <div className="mail-thread__manage">
          {threads.data?.length ? (
            <>
              <label className="field">
                <span>Цепочка для объединения</span>
                <Select
                  value={mergeSourceId}
                  onChange={(event) => setMergeSourceId(event.target.value)}
                >
                  <option value="">Выберите исходную цепочку</option>
                  {threads.data
                    .filter((thread) => thread.id !== selected?.thread_id)
                    .map((thread) => (
                      <option key={thread.id} value={thread.id}>
                        {thread.normalized_subject ?? "Без темы"} ·{" "}
                        {thread.message_count} писем
                      </option>
                    ))}
                </Select>
              </label>
              <button
                className="button button--text button--compact"
                type="button"
                disabled={!mergeSourceId || !selected?.thread_id}
                onClick={() =>
                  setConfirmation({
                    kind: "merge",
                    threadId: selected?.thread_id ?? "",
                  })
                }
              >
                Объединить с открытой цепочкой
              </button>
              <button
                className="button button--text button--compact"
                type="button"
                disabled={!selected?.thread_id || markThreadRead.isPending}
                onClick={() => {
                  if (selected?.thread_id)
                    markThreadRead.mutate(selected.thread_id);
                }}
              >
                Отметить цепочку прочитанной
              </button>
              <button
                className="button button--text button--compact"
                type="button"
                disabled={splitIds.length === 0}
                onClick={() => {
                  const threadId = entries.find((entry) =>
                    splitIds.includes(entry.id),
                  )?.thread_id;
                  if (threadId) setConfirmation({ kind: "split", threadId });
                }}
              >
                Разделить выбранные ({splitIds.length})
              </button>
            </>
          ) : null}
          {markThreadRead.isError ? (
            <Notice>
              {errorText(markThreadRead.error)}
            </Notice>
          ) : null}
        </div>
      ) : null}
      {selected ? (
        <article className="mail-thread__detail" aria-live="polite">
          <header>
            <div>
              <p className="eyebrow">
                {selected.direction === "incoming"
                  ? "Входящее письмо"
                  : "Исходящее письмо"}
              </p>
              <h4>{selected.subject ?? "Без темы"}</h4>
            </div>
            <CloseButton
              label="Закрыть письмо"
              onClick={() => setSelectedId(null)}
            />
          </header>
          {message.isPending ? (
            <p aria-busy="true">Открываем письмо…</p>
          ) : (
            <>
              <dl>
                <div>
                  <dt>От</dt>
                  <dd>{person(selected.from)}</dd>
                </div>
                <div>
                  <dt>Кому</dt>
                  <dd>{people(selected.to)}</dd>
                </div>
                {selected.direction === "incoming" &&
                (selected.sender_resolution_method === "conflict" ||
                  selected.sender_resolution_method === "unresolved") ? (
                  <div className="mail-thread__sender-state">
                    <dt>Контрагент</dt>
                    <dd>
                      {selected.sender_resolution_method === "conflict"
                        ? "Конфликт: автоматический выбор невозможен, нужно решение человека. "
                        : "Отправитель не сопоставлен ни с одним контрагентом. "}
                      <a
                        href={`/modules/document-workflow/counterparties?tab=inbox&message=${selected.id}`}
                      >
                        Разобрать письмо
                      </a>
                    </dd>
                  </div>
                ) : null}
                {selected.cc.length ? (
                  <div>
                    <dt>Получатели в копии (Cc)</dt>
                    <dd>{people(selected.cc)}</dd>
                  </div>
                ) : null}
                {selected.bcc?.length ? (
                  <div>
                    <dt>Скрытые получатели (Bcc)</dt>
                    <dd>{people(selected.bcc)}</dd>
                  </div>
                ) : null}
                {selected.in_reply_to ? (
                  <div>
                    <dt>Ответ на</dt>
                    <dd>
                      {parent
                        ? `${parent.subject ?? "Без темы"} — ${person(parent.from)}`
                        : selected.in_reply_to}
                    </dd>
                  </div>
                ) : null}
              </dl>
              <MailBody message={selected} />
              {canViewFiles && messageAttachments.isPending ? (
                <p aria-busy="true">Загружаем вложения письма…</p>
              ) : null}
              {canViewFiles && messageAttachments.isError ? (
                <Notice>
                  {errorText(messageAttachments.error)}
                  <button
                    className="button button--text button--compact"
                    type="button"
                    onClick={() => void messageAttachments.refetch()}
                  >
                    Повторить
                  </button>
                </Notice>
              ) : null}
              {canViewFiles && messageAttachments.data?.length ? (
                <section>
                  <h5>Вложения</h5>
                  <ul className="attachment-list">
                    {messageAttachments.data.map((attachment) => (
                      <li key={attachment.id}>
                        <span>
                          {attachment.name}
                          <small>
                            Источник: Email ·{" "}
                            {attachment.scan_status === "clean"
                              ? "проверено"
                              : attachment.scan_status}
                          </small>
                        </span>
                        <span className="inline-actions">
                          <button
                            className="button button--secondary button--compact"
                            type="button"
                            disabled={downloadAttachment.isPending}
                            onClick={() =>
                              downloadAttachment.mutate(attachment)
                            }
                          >
                            Скачать
                          </button>
                          {canLinkFiles &&
                          attachment.available_actions?.includes(
                            "link_to_document",
                          ) ? (
                            linkedVersion?.attachmentId === attachment.id ? (
                              <button
                                className="button button--text button--compact"
                                type="button"
                                disabled={unlinkAttachment.isPending}
                                onClick={() =>
                                  unlinkAttachment.mutate(
                                    linkedVersion.versionId,
                                  )
                                }
                              >
                                Отвязать версию {linkedVersion.versionNumber}
                              </button>
                            ) : (
                              <button
                                className="button button--text button--compact"
                                type="button"
                                disabled={linkAttachment.isPending}
                                onClick={() =>
                                  linkAttachment.mutate(attachment)
                                }
                              >
                                {attachmentTargetId
                                  ? "Добавить версию"
                                  : "Добавить в файлы"}
                              </button>
                            )
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {canLinkFiles ? (
                    <label className="field mail-thread__attachment-target">
                      <span>Куда добавить вложение</span>
                      <Select
                        value={attachmentTargetId}
                        onChange={(event) =>
                          setAttachmentTargetId(event.target.value)
                        }
                      >
                        <option value="">Как новый логический файл</option>
                        {existingFiles?.map((file) => (
                          <option key={file.id} value={file.id}>
                            {file.display_name}
                          </option>
                        ))}
                      </Select>
                    </label>
                  ) : null}
                  {linkAttachment.isError ||
                  unlinkAttachment.isError ||
                  downloadAttachment.isError ? (
                    <Notice>
                      {errorText(
                        linkAttachment.error ??
                          unlinkAttachment.error ??
                          downloadAttachment.error,
                      )}
                    </Notice>
                  ) : null}
                </section>
              ) : null}
              {canManageThreads ? (
                <details className="mail-thread__links">
                  <summary>Управление связью письма</summary>
                  <div className="mail-thread__links-body">
                    <label className="field">
                      <span>UUID другого документа</span>
                      <input
                        value={linkedDocumentId}
                        onChange={(event) =>
                          setLinkedDocumentId(event.target.value)
                        }
                        placeholder="UUID"
                      />
                    </label>
                    <button
                      className="button button--secondary button--compact"
                      type="button"
                      disabled={!linkedDocumentId || link.isPending}
                      onClick={() => link.mutate()}
                    >
                      Привязать
                    </button>
                    <button
                      className="button button--text button--compact"
                      type="button"
                      disabled={unlink.isPending}
                      onClick={() => unlink.mutate()}
                    >
                      Отвязать от этого документа
                    </button>
                    {link.isError || unlink.isError ? (
                      <Notice>
                        {errorText(link.error ?? unlink.error)}
                      </Notice>
                    ) : null}
                  </div>
                </details>
              ) : null}
            </>
          )}
        </article>
      ) : null}
      {compose && canReply ? (
        <form
          className="mail-thread__compose"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            const errors = {
              to: addressListError(to, "Кому", { required: true }),
              bcc: addressListError(bcc, "Скрытые получатели (Bcc)", {
                required: false,
              }),
            };
            setAddressErrors(errors);
            if (errors.to !== null) {
              toInput.current?.focus();
              return;
            }
            if (errors.bcc !== null) {
              bccInput.current?.focus();
              return;
            }
            reply.mutate();
          }}
        >
          <header>
            <h4>Ответ контрагенту</h4>
            <CloseButton
              label="Закрыть форму ответа"
              onClick={() => setCompose(false)}
            />
          </header>
          <label className="field">
            <span>Кому</span>
            <small>Эти адресаты видны всем получателям письма.</small>
            <input
              ref={toInput}
              required
              aria-label="Кому"
              aria-invalid={addressErrors.to === null ? undefined : true}
              aria-describedby={
                addressErrors.to === null ? undefined : "mail-to-error"
              }
              value={to}
              onChange={(event) => {
                submissionKey.current = null;
                setAddressErrors((state) => ({ ...state, to: null }));
                setTo(event.target.value);
              }}
              placeholder="name@example.com, second@example.com"
            />
            {addressErrors.to === null ? null : (
              <small
                id="mail-to-error"
                className="cp-address-error"
                role="alert"
              >
                {addressErrors.to}
              </small>
            )}
          </label>
          {services.counterparties === undefined ? null : (
            <CounterpartyRecipientPicker
              service={services.counterparties}
              disabled={reply.isPending}
              onApply={(emails) => {
                submissionKey.current = null;
                setAddressErrors((state) => ({ ...state, to: null }));
                // К введённому дописываем, а не заменяем: ручной ввод не теряется.
                setTo((current) => mergeAddresses(current, emails));
              }}
            />
          )}
          <label className="field">
            <span>Скрытые получатели (Bcc)</span>
            <small>Эти адресаты не видны остальным получателям.</small>
            <input
              ref={bccInput}
              aria-label="Скрытые получатели (Bcc)"
              aria-invalid={addressErrors.bcc === null ? undefined : true}
              aria-describedby={
                addressErrors.bcc === null ? undefined : "mail-bcc-error"
              }
              value={bcc}
              onChange={(event) => {
                submissionKey.current = null;
                setAddressErrors((state) => ({ ...state, bcc: null }));
                setBcc(event.target.value);
              }}
            />
            {addressErrors.bcc === null ? null : (
              <small
                id="mail-bcc-error"
                className="cp-address-error"
                role="alert"
              >
                {addressErrors.bcc}
              </small>
            )}
          </label>
          <label className="field">
            <span>Тема</span>
            <input
              required
              value={subject}
              onChange={(event) => {
                submissionKey.current = null;
                setSubject(event.target.value);
              }}
            />
          </label>
          <label className="field">
            <span>Текст</span>
            <textarea
              required
              value={text}
              onChange={(event) => {
                submissionKey.current = null;
                setText(event.target.value);
              }}
            />
          </label>
          <div className="mail-thread__existing-file">
            <label className="field">
              <span>Существующий файл документа</span>
              <Select
                value={existingFileId}
                onChange={(event) => setExistingFileId(event.target.value)}
              >
                <option value="">Выберите проверенный файл</option>
                {existingFiles
                  ?.filter((file) => file.current_version?.file_id)
                  .map((file) => (
                    <option
                      key={file.id}
                      value={file.current_version?.file_id ?? ""}
                    >
                      {file.display_name}
                    </option>
                  ))}
              </Select>
            </label>
            <button
              className="button button--secondary button--compact"
              type="button"
              disabled={!existingFileId || reply.isPending}
              onClick={addExistingFile}
            >
              Прикрепить файл
            </button>
          </div>
          <AttachmentPicker
            services={services}
            attachments={attachments}
            onChange={(next) => {
              submissionKey.current = null;
              setAttachments(next);
            }}
            accessScopeId={accessScopeId}
            disabled={reply.isPending}
          />
          {reply.isError ? (
            <Notice>
              {errorText(reply.error)}
            </Notice>
          ) : null}
          <button className="button button--primary" disabled={reply.isPending}>
            {reply.isPending ? "Ставим в очередь…" : "Отправить ответ"}
          </button>
        </form>
      ) : null}
      {confirmation ? (
        <div className="mail-thread__confirm-backdrop">
          <div
            className="mail-thread__confirm"
            role="alertdialog"
            aria-modal="true"
            aria-label="Подтверждение действия"
            onKeyDown={(event) => {
              if (
                event.key === "Escape" &&
                !outboxAction.isPending &&
                !threadAction.isPending
              )
                setConfirmation(null);
            }}
          >
            <p>
              {confirmation.kind === "split"
                ? "Подтвердите разделение цепочки. Письма, вложения и история сохранятся сервером."
                : confirmation.kind === "merge"
                  ? "Подтвердите объединение цепочек."
                  : confirmation.kind === "retry" && confirmation.uncertain
                    ? "Доставка не подтверждена: повтор может создать дубликат. Подтвердите осознанно."
                    : "Укажите причину действия для аудита."}
            </p>
            <label className="field">
              <span>Причина</span>
              <textarea
                ref={reasonRef}
                required
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            {outboxAction.isError || threadAction.isError ? (
              <Notice>
                {errorText(outboxAction.error ?? threadAction.error)}
              </Notice>
            ) : null}
            <button
              className="button button--primary"
              type="button"
              disabled={
                !reason.trim() ||
                outboxAction.isPending ||
                threadAction.isPending
              }
              onClick={() => {
                if (confirmation.kind === "retry")
                  outboxAction.mutate({
                    id: confirmation.outboxId,
                    action: confirmation.uncertain ? "confirm-retry" : "retry",
                  });
                else if (confirmation.kind === "cancel")
                  outboxAction.mutate({
                    id: confirmation.outboxId,
                    action: "cancel",
                  });
                else threadAction.mutate(confirmation);
              }}
            >
              Подтвердить
            </button>
            <button
              className="button button--text"
              type="button"
              onClick={() => setConfirmation(null)}
            >
              Отмена
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
